/**
 * The share image: a 1080×1920 PNG drawn on a canvas, laid out like the
 * concept video's share card on the picked theme, so it saves without any
 * extra library. Colours come from `wrapped.css` at draw time.
 */

import { count, hourLabel, lastDay, longDate } from "../cards/shared";
import { PERSON_COLOURS, shareThemeValues, tokenValue, type ThemeId } from "../theme";
import type { Period, PersonStory } from "../types";

export const WIDTH = 1080;
export const HEIGHT = 1920;
export const MARGIN = 72;
export const MAX_STATS = 4;

export type StatId = "messages" | "top_person" | "word" | "emoji" | "awards" | "personality" | "peak_hour" | "streak";
export type ShareOptions = { theme: ThemeId; stats: StatId[]; showNames: boolean };

/** The pick chips' labels, as the video has them. */
export const STAT_LABELS: Record<StatId, string> = {
  messages: "Messages",
  top_person: "Top person",
  word: "Word",
  emoji: "Emoji",
  awards: "Awards",
  personality: "Personality",
  peak_hour: "Peak hour",
  streak: "Streak",
};
const ORDER = Object.keys(STAT_LABELS) as StatId[];
/** A fresh image shows the video's picks first, then whatever else has data. */
const FIRST: StatId[] = ["messages", "top_person", "word", "personality", "emoji", "awards", "peak_hour", "streak"];
/** Stats that take half a row of the grid; messages and personality get their own section, awards a full row. */
const HALF: StatId[] = ["top_person", "word", "emoji", "peak_hour", "streak"];

export function availableStats(story: PersonStory): StatId[] {
  const you = story.you;
  const has: Record<StatId, boolean> = {
    messages: true,
    top_person: you.top_people.length > 0,
    word: Boolean(you.words.signature || you.words.top.length),
    emoji: you.emoji.length > 0,
    awards: you.awards.length > 0,
    personality: true,
    peak_hour: true,
    streak: you.streak !== null,
  };
  return ORDER.filter((stat) => has[stat]);
}

export function defaultOptions(story: PersonStory): ShareOptions {
  const has = availableStats(story);
  const picked = FIRST.filter((stat) => has.includes(stat)).slice(0, MAX_STATS);
  return { theme: "ink", stats: ORDER.filter((stat) => picked.includes(stat)), showNames: true };
}

/** Add or remove a stat, keeping the image's order fixed and never more than four. */
export function toggleStat(stats: StatId[], stat: StatId): StatId[] {
  if (stats.includes(stat)) return stats.filter((s) => s !== stat);
  if (stats.length >= MAX_STATS) return stats;
  return ORDER.filter((s) => s === stat || stats.includes(s));
}

type Slot = { label: string; values: string[]; emoji?: boolean };

/** A grid stat's label and its values; most have one value, awards have one per award. */
function slot(story: PersonStory, stat: StatId, showNames: boolean): Slot {
  const you = story.you;
  switch (stat) {
    case "top_person":
      return { label: "Top person", values: [showNames ? you.top_people[0].name : "a friend"] };
    case "word":
      return { label: "Word", values: [you.words.signature ?? you.words.top[0].word] };
    case "emoji":
      return { label: "Top emoji", values: [you.emoji.slice(0, 3).map((e) => e.emoji).join(" ")], emoji: true };
    case "awards":
      return { label: "Awards", values: you.awards.map((a) => a.name) };
    case "peak_hour":
      return { label: "Peak hour", values: [hourLabel(you.clock.peak_hour)] };
    case "streak":
      return { label: "Longest streak", values: [`${you.streak?.days ?? 0} days`] };
    case "messages":
      return { label: "Messages", values: [count(you.messages.total)] };
    case "personality":
      return { label: "Personality", values: [you.personality.type] };
  }
}

/** "50" a day, or "0.4" a day for someone quiet, as the messages card says it. */
const perDay = (n: number) => (n >= 10 ? Math.round(n).toLocaleString("en-US") : String(n));
const dates = (period: Period) => `${longDate(period.start)} – ${longDate(lastDay(period.end))}`;
const wrappedLabel = (period: Period) => `WRAPPED ${period.title.toUpperCase()}`;

/** Everything the image says, so the fonts can fetch every letter it needs before drawing. */
export function imageText(story: PersonStory, period: Period, opts: ShareOptions): string {
  const stats = opts.stats.flatMap((stat) => {
    const { label, values } = slot(story, stat, opts.showNames);
    return [label.toUpperCase(), ...values];
  });
  const day = `${perDay(story.you.messages.per_day)} a day`;
  return [story.person.name, wrappedLabel(period), day, dates(period), ...stats].join(" ");
}

// ---------- type ----------

// A canvas can't read `var(--wr-font-…)`, so it gets the families themselves.
const FAMILIES = {
  display: '"Bricolage Grotesque Variable", sans-serif',
  mono: '"JetBrains Mono Variable", monospace',
  emoji: '"Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif',
};
type Face = keyof typeof FAMILIES;

/** Wait for both faces (and the letters the image uses), so the PNG never bakes in a fallback font. */
export function loadFonts(text: string): Promise<void> {
  const fonts = typeof document === "undefined" ? undefined : document.fonts;
  if (!fonts || typeof fonts.load !== "function") return Promise.resolve();
  return Promise.all([fonts.load(`800 64px ${FAMILIES.display}`, text), fonts.load(`700 24px ${FAMILIES.mono}`, text)]).then(
    () => undefined,
    () => undefined,
  );
}

function setFont(ctx: CanvasRenderingContext2D, weight: number, size: number, face: Face) {
  const family = FAMILIES[face];
  ctx.font = `${weight} ${size}px ${family}`;
  // Checked on a plain object, so TypeScript doesn't narrow the context away.
  const canStretch = "fontStretch" in (ctx as object);
  if (face !== "display") {
    if (canStretch) ctx.fontStretch = "normal";
    return;
  }
  // The video sets its display type condensed. Browsers without fontStretch may
  // still take it in the shorthand; one that takes neither keeps the plain width.
  if (canStretch) ctx.fontStretch = "condensed";
  else ctx.font = `${weight} condensed ${size}px ${family}`;
}

function spacing(ctx: CanvasRenderingContext2D, px: number) {
  if ("letterSpacing" in (ctx as object)) ctx.letterSpacing = `${px}px`;
}

/** Break `text` into lines no wider than `width` at the context's current font. */
function wrap(ctx: CanvasRenderingContext2D, text: string, width: number): string[] {
  const lines: string[] = [];
  let current = "";
  for (const word of text.split(" ")) {
    const attempt = current ? `${current} ${word}` : word;
    if (current && ctx.measureText(attempt).width > width) {
      lines.push(current);
      current = word;
    } else {
      current = attempt;
    }
  }
  if (current) lines.push(current);
  return lines;
}

/** Shorten `text` with an ellipsis until it fits `width` at the current font. */
function ellipsize(ctx: CanvasRenderingContext2D, text: string, width: number): string {
  if (ctx.measureText(text).width <= width) return text;
  let cut = text;
  while (cut.length > 1 && ctx.measureText(`${cut}…`).width > width) cut = cut.slice(0, -1);
  return `${cut.trimEnd()}…`;
}

/** The largest size from `max` down to `min` for which `fits` holds, or null when none does. */
function largest(max: number, min: number, fits: (size: number) => boolean): number | null {
  for (let size = Math.floor(max); size >= min; size -= 2) if (fits(size)) return size;
  return null;
}

/**
 * Big text in at most `maxLines` lines of `width`: the largest size that fits,
 * down to `min`. Shrinking comes first; an ellipsis only when even that can't fit.
 */
function fitLines(ctx: CanvasRenderingContext2D, text: string, width: number, max: number, min: number, maxLines: number, face: Face = "display") {
  const fits = (size: number) => {
    setFont(ctx, 800, size, face);
    const lines = wrap(ctx, text, width);
    return lines.length <= maxLines && lines.every((l) => ctx.measureText(l).width <= width);
  };
  const size = largest(max, min, fits);
  if (size !== null) {
    setFont(ctx, 800, size, face);
    return { size, lines: wrap(ctx, text, width) };
  }
  setFont(ctx, 800, min, face);
  const all = wrap(ctx, text, width);
  const kept = [...all.slice(0, maxLines - 1), all.slice(maxLines - 1).join(" ")];
  return { size: min, lines: kept.map((l) => ellipsize(ctx, l, width)) };
}

// ---------- layout ----------

// The video's card is 900 wide; the image is 1080, so its sizes are the card's times 1.2.
const CONTENT = WIDTH - MARGIN * 2;
const COL_GAP = 36;
const COL = (CONTENT - COL_GAP) / 2;
const ROW_GAP = 36;
const AV = 134;
const LAB = 29;
const LAB_BASE = Math.round(LAB * 0.72);
/** Cap height and descender, as shares of the font size; close enough for both faces. */
const CAP = 0.72;
const DESC = 0.2;
// Room at the top and bottom for a story app's own header and reply bar.
const TOP = 150;
const FOOT_BASE = HEIGHT - 150;
const MAX_GAP = 120;

type Colours = ReturnType<typeof shareThemeValues>;
type Block = { height: number; draw: (top: number) => void };

function label(ctx: CanvasRenderingContext2D, text: string, x: number, baseline: number, width: number, colour: string) {
  setFont(ctx, 700, LAB, "mono");
  spacing(ctx, LAB * 0.08);
  ctx.globalAlpha = 0.62;
  ctx.fillStyle = colour;
  ctx.fillText(ellipsize(ctx, text.toUpperCase(), width), x, baseline);
  ctx.globalAlpha = 1;
  spacing(ctx, 0);
}

function stack(blocks: Block[], gap: number): Block {
  return {
    height: blocks.reduce((sum, b) => sum + b.height, 0) + gap * (blocks.length - 1),
    draw(top) {
      let y = top;
      for (const b of blocks) {
        b.draw(y);
        y += b.height + gap;
      }
    },
  };
}

function headBlock(
  ctx: CanvasRenderingContext2D,
  name: string,
  period: Period,
  c: Colours,
  avatar: string,
  picture: CanvasImageSource | null,
): Block {
  const x = MARGIN + AV + 29;
  const width = WIDTH - MARGIN - x;
  const size = largest(77, 44, (s) => {
    setFont(ctx, 800, s, "display");
    return ctx.measureText(name).width <= width;
  });
  setFont(ctx, 800, size ?? 44, "display");
  const shown = size === null ? ellipsize(ctx, name, width) : name;
  return {
    height: AV,
    draw(top) {
      ctx.fillStyle = avatar;
      ctx.beginPath();
      ctx.arc(MARGIN + AV / 2, top + AV / 2, AV / 2, 0, Math.PI * 2);
      ctx.fill();
      if (picture) {
        // Their profile picture, cut to the circle.
        ctx.save();
        ctx.clip();
        ctx.drawImage(picture, MARGIN, top, AV, AV);
        ctx.restore();
      } else {
        ctx.fillStyle = tokenValue("ink") || c.bg;
        setFont(ctx, 800, 74, "display");
        ctx.textAlign = "center";
        ctx.fillText([...name][0]?.toUpperCase() ?? "?", MARGIN + AV / 2, top + AV / 2 + (74 * CAP) / 2);
        ctx.textAlign = "left";
      }

      ctx.fillStyle = c.fg;
      setFont(ctx, 800, size ?? 44, "display");
      ctx.fillText(shown, x, top + 70);
      setFont(ctx, 700, 31, "mono");
      spacing(ctx, 2.5);
      ctx.fillStyle = c.accent;
      ctx.fillText(ellipsize(ctx, wrappedLabel(period), width), x, top + 118);
      spacing(ctx, 0);
    },
  };
}

function messagesBlock(ctx: CanvasRenderingContext2D, messages: PersonStory["you"]["messages"], c: Colours): Block {
  const total = count(messages.total);
  const day = `${perDay(messages.per_day)} a day`;
  const measure = (text: string, weight: number, size: number) => {
    setFont(ctx, weight, size, "display");
    return ctx.measureText(text).width;
  };
  const dayWidth = measure(day, 700, 53);
  // The number shrinks to leave room for "a day" beside it; past that, "a day" moves underneath.
  const beside = largest(228, 112, (s) => measure(total, 800, s) + 24 + dayWidth <= CONTENT);
  const size = beside ?? largest(228, 40, (s) => measure(total, 800, s) <= CONTENT) ?? 40;
  const base = LAB_BASE + 30 + CAP * size;
  const under = beside === null ? 20 + CAP * 53 : 0;
  return {
    height: base + under + DESC * 53,
    draw(top) {
      label(ctx, "Messages", MARGIN, top + LAB_BASE, CONTENT, c.fg);
      ctx.fillStyle = c.fg;
      const width = measure(total, 800, size);
      ctx.fillText(total, MARGIN, top + base);
      setFont(ctx, 700, 53, "display");
      if (beside === null) ctx.fillText(day, MARGIN, top + base + under);
      else ctx.fillText(day, MARGIN + width + 24, top + base);
    },
  };
}

/** One grid row: one or two stats side by side, each value one line in the accent. */
function pairRow(ctx: CanvasRenderingContext2D, slots: Slot[], c: Colours): Block {
  const valueTop = LAB_BASE + 22;
  const fitted = slots.map((s) => {
    const face: Face = s.emoji ? "emoji" : "display";
    const { size, lines } = fitLines(ctx, s.values[0], COL, s.emoji ? 89 : 96, 52, 1, face);
    return { ...s, face, size, text: lines[0] };
  });
  return {
    height: valueTop + (CAP + DESC) * 96,
    draw(top) {
      fitted.forEach((s, i) => {
        const x = MARGIN + i * (COL + COL_GAP);
        label(ctx, s.label, x, top + LAB_BASE, COL, c.fg);
        setFont(ctx, 800, s.size, s.face);
        ctx.fillStyle = c.accent;
        // An emoji stands a little taller than a capital, so its baseline sits lower.
        ctx.fillText(s.text, x, top + valueTop + (s.emoji ? 0.84 : CAP) * s.size);
      });
    },
  };
}

/** Awards take a full row, one per line, and their names are never cut: they shrink, then wrap. */
function awardsRow(ctx: CanvasRenderingContext2D, names: string[], c: Colours): Block {
  const valueTop = LAB_BASE + 22;
  const room = (520 - valueTop) / Math.max(1, names.length);
  const fits = (s: number) => {
    setFont(ctx, 800, s, "display");
    return names.every((n) => ctx.measureText(n).width <= CONTENT);
  };
  const size = largest(Math.min(80, room), 44, fits) ?? 44;
  setFont(ctx, 800, size, "display");
  const lines = names.flatMap((n) => wrap(ctx, n, CONTENT));
  return {
    height: valueTop + CAP * size + Math.max(0, lines.length - 1) * size + DESC * size,
    draw(top) {
      label(ctx, "Awards", MARGIN, top + LAB_BASE, CONTENT, c.fg);
      setFont(ctx, 800, size, "display");
      ctx.fillStyle = c.accent;
      lines.forEach((l, i) => ctx.fillText(l, MARGIN, top + valueTop + CAP * size + i * size));
    },
  };
}

function personalityBlock(ctx: CanvasRenderingContext2D, type: string, c: Colours): Block {
  const valueTop = LAB_BASE + 26;
  const { size, lines } = fitLines(ctx, type, CONTENT, 130, 64, 3);
  const step = 0.88 * size;
  return {
    height: valueTop + CAP * size + (lines.length - 1) * step + DESC * size,
    draw(top) {
      label(ctx, "Personality", MARGIN, top + LAB_BASE, CONTENT, c.fg);
      setFont(ctx, 800, size, "display");
      ctx.fillStyle = c.accent2;
      lines.forEach((l, i) => ctx.fillText(l, MARGIN, top + valueTop + CAP * size + i * step));
    },
  };
}

/** The canvas can't read `var(--wr-…)` either: look the person's colour up by its token. */
function realColour(colour: string, fallback: string) {
  const token = /^var\(--wr-([\w-]+)\)$/.exec(colour)?.[1];
  return (token && tokenValue(token)) || fallback;
}

/**
 * Draw the image: the head (avatar, name, "WRAPPED 2026") at the top, the
 * period at the bottom, and the picked stats centred between them, as the
 * video's card stacks them: messages, the grid, then the personality.
 * `avatar` is the person's colour as the page has it, e.g. `var(--wr-blurple)`;
 * `picture` their profile picture, already loaded, drawn over it when there is one.
 */
export function drawShare(
  ctx: CanvasRenderingContext2D,
  story: PersonStory,
  period: Period,
  opts: ShareOptions,
  avatar: string = PERSON_COLOURS[0],
  picture: CanvasImageSource | null = null,
) {
  const c = shareThemeValues(opts.theme);
  const picked = (stat: StatId) => opts.stats.includes(stat);
  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";
  ctx.globalAlpha = 1;
  ctx.fillStyle = c.bg;
  ctx.fillRect(0, 0, WIDTH, HEIGHT);

  const head = headBlock(ctx, story.person.name, period, c, realColour(avatar, c.accent), picture);
  head.draw(TOP);

  const rows: Block[] = [];
  const halves = opts.stats.filter((stat) => HALF.includes(stat)).map((stat) => slot(story, stat, opts.showNames));
  for (let i = 0; i < halves.length; i += 2) rows.push(pairRow(ctx, halves.slice(i, i + 2), c));
  if (picked("awards")) rows.push(awardsRow(ctx, story.you.awards.map((a) => a.name), c));

  const sections = [
    picked("messages") ? messagesBlock(ctx, story.you.messages, c) : null,
    rows.length ? stack(rows, ROW_GAP) : null,
    picked("personality") ? personalityBlock(ctx, story.you.personality.type, c) : null,
  ].filter((b): b is Block => b !== null);

  const areaTop = TOP + AV;
  const areaBottom = FOOT_BASE - 26 * CAP;
  const free = areaBottom - areaTop - sections.reduce((sum, b) => sum + b.height, 0);
  const gap = Math.max(0, Math.min(MAX_GAP, free / (sections.length + 1)));
  let y = areaTop + (free - gap * (sections.length - 1)) / 2;
  for (const section of sections) {
    section.draw(y);
    y += section.height + gap;
  }

  setFont(ctx, 600, 26, "mono");
  spacing(ctx, 1);
  ctx.globalAlpha = 0.62;
  ctx.fillStyle = c.fg;
  ctx.fillText(ellipsize(ctx, dates(period), CONTENT), MARGIN, FOOT_BASE);
  ctx.globalAlpha = 1;
  spacing(ctx, 0);
}

// ---------- saving and sharing ----------

/** The canvas as a PNG, made ahead of the tap: phones only allow a share sheet straight from one. */
/** A same-site picture, decoded and ready to draw; null if there's none or it won't load. */
export function loadPicture(url: string | null): Promise<HTMLImageElement | null> {
  if (!url || typeof Image === "undefined") return Promise.resolve(null);
  const img = new Image();
  img.src = url;
  if (typeof img.decode !== "function") return Promise.resolve(null);
  return img.decode().then(
    () => img,
    () => null,
  );
}

export function renderImage(canvas: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise((done) => {
    try {
      canvas.toBlob(done, "image/png");
    } catch {
      done(null);
    }
  });
}

const fileName = (title: string) => `wrapped-${title}.png`;

export function downloadImage(blob: Blob, title: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName(title);
  link.click();
  // Safari cancels a download whose address is revoked straight away.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export const canShareImages = () =>
  typeof navigator.canShare === "function" &&
  navigator.canShare({ files: [new File([], fileName("test"), { type: "image/png" })] });

/** Open the share sheet. Call it straight from the tap, with no await before it. */
export function shareImage(blob: Blob, title: string): Promise<void> {
  const file = new File([blob], fileName(title), { type: "image/png" });
  return navigator.share({ files: [file], title: `My Wrapped ${title}` });
}
